import {
	BrandFileType,
	useConfirmBrandFileUploadMutation,
	useGenerateBrandFileUploadUrlMutation,
} from '@codezo/graphql';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';
import { FileInput } from '../../components/ui/file-input';
import { useFilePrepare } from '../../hooks/useFilePrepare';
import {
	BRAND_DOC_ACCEPT,
	BRAND_DOC_LABEL,
	BRAND_IMAGE_ACCEPT,
	BRAND_IMAGE_LABEL,
	FONT_ACCEPT,
	FONT_LABEL,
} from '../../lib/file-types';

/**
 * Font is deliberately absent.
 *
 * This modal confirms through `confirmBrandFileUpload`, which writes to
 * `brandFiles`. A font belongs in `fontFiles` — a different column, and the only
 * one the typography section reads — so a font uploaded here stores fine and
 * then never appears as a typeface. Fonts go through `UploadBrandFontModal`,
 * reached from Typography → Add font.
 */
const fileTypeOptions = [
	{ value: BrandFileType.Logo, label: 'Logo' },
	{ value: BrandFileType.Icon, label: 'Icon' },
	{ value: BrandFileType.Image, label: 'Image' },
	{ value: BrandFileType.Document, label: 'Document' },
];

/**
 * Title and hint per pinned type.
 *
 * A dialog opened by "Add logo" that announces "Upload asset" reads as the
 * wrong dialog, so the copy follows whatever the caller pinned. `any` is the
 * Assets section, where the type really is still a question.
 */
const PINNED_COPY: Record<BrandFileType | 'any', { title: string; hint: string }> = {
	[BrandFileType.Logo]: {
		title: 'Add logo',
		hint: 'Add a logo variant — the mark, a wordmark, a monochrome cut.',
	},
	[BrandFileType.Font]: {
		title: 'Add font',
		hint: 'Upload a font file. Its family becomes a typeface on this brand.',
	},
	[BrandFileType.Icon]: { title: 'Add icon', hint: 'Upload an icon for this brand.' },
	[BrandFileType.Image]: { title: 'Add image', hint: 'Upload an image for this brand.' },
	[BrandFileType.Document]: {
		title: 'Add document',
		hint: 'Upload a document — a brand book, a set of guidelines.',
	},
	any: {
		title: 'Upload asset',
		hint: 'Upload a logo variant, icon, image, document or font.',
	},
};

/** Accepted files depend on the selected brand asset type. */
function acceptForBrandType(type: BrandFileType): { accept: string; label: string } {
	if (type === BrandFileType.Document)
		return { accept: BRAND_DOC_ACCEPT, label: BRAND_DOC_LABEL };
	if (type === BrandFileType.Font) return { accept: FONT_ACCEPT, label: FONT_LABEL };
	return { accept: BRAND_IMAGE_ACCEPT, label: BRAND_IMAGE_LABEL };
}

export function UploadBrandAssetModal({
	open,
	onOpenChange,
	brandId,
	onSuccess,
	/**
	 * Pins the asset type and hides the picker.
	 *
	 * Opened from the Logo section the answer is already "logo" — asking again
	 * is a required field with one correct value, and getting it wrong files the
	 * upload somewhere the user will not find it. The Assets section opens the
	 * same modal without this and keeps the picker, because there the type is a
	 * real question.
	 */
	fixedType,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	brandId: string;
	onSuccess?: () => void;
	fixedType?: BrandFileType;
}) {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [label, setLabel] = useState('');
	const [fileType, setFileType] = useState<BrandFileType>(fixedType ?? BrandFileType.Logo);
	const [uploading, setUploading] = useState(false);
	const { prepare, converting } = useFilePrepare();

	const [generateUploadUrl] = useGenerateBrandFileUploadUrlMutation();
	const [confirmUpload] = useConfirmBrandFileUploadMutation();

	/**
	 * Re-seed the pinned type every time the dialog opens.
	 *
	 * `useState` runs its initialiser once and the dialog keeps its tree between
	 * openings, so the type carried over from whichever section opened it last.
	 * Open the logo uploader, close it, then open "Add font" and `fileType` was
	 * still `logo` — which means the accept list was still the logo one, and the
	 * picker happily offered images for a font slot.
	 */
	useEffect(() => {
		if (!open) return;
		setFileType(fixedType ?? BrandFileType.Logo);
	}, [open, fixedType]);

	/* Both entry points validate against this, not just the picker's `accept`
	   hint: `FileInput` runs `prepareFiles(selected, accept)` and the drop
	   handler calls `prepare` with the same list, so a dragged-in PNG is
	   rejected rather than queued. */
	const brandAccept = acceptForBrandType(fileType);

	/** Switching asset type clears a now-mismatched file. */
	const handleTypeChange = (next: BrandFileType) => {
		setFileType(next);
		setFile(null);
		setPreview((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
	};

	const resetForm = () => {
		setFile(null);
		setPreview((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return null;
		});
		setLabel('');
		setFileType(fixedType ?? BrandFileType.Logo);
	};

	const handleFileSelect = useCallback((selected: File) => {
		setFile(selected);
		setLabel((prev) => prev || selected.name.replace(/\.[^.]+$/, ''));

		// Revoke previous preview to prevent memory leak
		setPreview((prev) => {
			if (prev) URL.revokeObjectURL(prev);
			return selected.type.startsWith('image/') ? URL.createObjectURL(selected) : null;
		});
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			const picked = e.dataTransfer.files[0];
			if (!picked) return;
			const [dropped] = await prepare([picked], acceptForBrandType(fileType).accept);
			if (dropped) handleFileSelect(dropped);
		},
		[handleFileSelect, prepare, fileType]
	);

	const handleSubmit = async () => {
		if (!file || !label.trim()) return;
		setUploading(true);

		/* Keep the real extension on the stored name. It's what the format badge
		 * and the file-type icon read, and the backend falls back to it when the
		 * browser reports a vague mime type — browsers routinely send .ai as
		 * application/octet-stream — so dropping it can cost the file its
		 * server-rendered thumbnail. */
		const trimmedLabel = label.trim();
		const ext = file.name.match(/\.([a-z0-9]{1,5})$/i)?.[1]?.toLowerCase() ?? '';
		const storedName =
			ext && !trimmedLabel.toLowerCase().endsWith(`.${ext}`)
				? `${trimmedLabel}.${ext}`
				: trimmedLabel;

		try {
			// 1. Get upload URL
			const { data: urlData } = await generateUploadUrl({
				variables: {
					input: {
						brandId,
						brandFileType: fileType,
						fileName: file.name,
						fileSize: file.size,
						fileType: file.type,
					},
				},
			});

			const uploadUrl = urlData?.generateBrandFileUploadUrl?.uploadUrl;
			const blobUrl = urlData?.generateBrandFileUploadUrl?.blobUrl;
			const fileId = urlData?.generateBrandFileUploadUrl?.fileId;

			if (!uploadUrl || !blobUrl || !fileId) {
				throw new Error('Failed to generate upload URL');
			}

			// 2. Upload to Azure
			const res = await fetch(uploadUrl, {
				method: 'PUT',
				headers: {
					'x-ms-blob-type': 'BlockBlob',
					'Content-Type': file.type,
				},
				body: file,
			});
			if (!res.ok) throw new Error('Failed to upload file');

			// 3. Confirm upload
			await confirmUpload({
				variables: {
					input: {
						brandId,
						fileId,
						blobUrl,
						brandFileType: fileType,
						fileName: storedName,
						fileSize: file.size,
						fileType: file.type,
					},
				},
			});

			toast.success('Asset uploaded');
			resetForm();
			onOpenChange(false);
			onSuccess?.();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Upload failed');
		} finally {
			setUploading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{PINNED_COPY[fixedType ?? 'any'].title}</DialogTitle>
					<DialogDescription>{PINNED_COPY[fixedType ?? 'any'].hint}</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-5">
							{/* Drop zone */}
							<FileInput
								onFiles={(files) => {
									const f = files[0];
									if (f) handleFileSelect(f);
								}}
								accept={brandAccept.accept}
							>
								{({ open, converting: fileConverting }) => (
									<div
										onDragOver={(e) => e.preventDefault()}
										onDrop={handleDrop}
										onClick={open}
										className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-line-1 bg-surface-fill/50 px-4 py-8 text-center transition-colors hover:border-line-2 hover:bg-surface-hover"
									>
										{preview ? (
											<div className="relative">
												<img
													src={preview}
													alt="Preview"
													className="max-h-24 rounded-lg border border-line-1 object-contain"
												/>
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setFile(null);
														setPreview(null);
													}}
													className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-surface text-text-3 shadow-sm ring-1 ring-line-1 hover:text-text-1"
												>
													<X size={10} />
												</button>
											</div>
										) : file ? (
											<div className="flex items-center gap-2">
												<ImageIcon size={16} className="text-text-3" />
												<span className="text-sm text-text-2">
													{file.name}
												</span>
												<button
													type="button"
													onClick={(e) => {
														e.stopPropagation();
														setFile(null);
													}}
													className="text-text-4 hover:text-text-1"
												>
													<X size={12} />
												</button>
											</div>
										) : (
											<>
												<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface text-text-3">
													<Upload size={18} />
												</div>
												<div>
													<div className="text-sm font-medium text-text-2">
														{converting || fileConverting
															? 'Converting…'
															: 'Drop a file here or click to browse'}
													</div>
													<div className="mt-1 text-xs text-text-4">
														{brandAccept.label}
													</div>
												</div>
											</>
										)}
									</div>
								)}
							</FileInput>

							{!fixedType && (
								<Field label="Asset type" required>
									<SelectPopover
										value={fileType}
										onChange={(v) => handleTypeChange(v as BrandFileType)}
										options={fileTypeOptions}
										placeholder="Select type"
									/>
								</Field>
							)}

							<Field label="Label" required>
								<Input
									value={label}
									onChange={(e) => setLabel(e.target.value)}
									placeholder="e.g. Primary mark"
								/>
							</Field>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!file || !label.trim() || uploading}>
							{uploading ? 'Uploading...' : 'Upload'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
